<template>
  <div class="pos-page">
    <v-app-bar dense flat color="white" class="pos-toolbar">
      <v-icon left color="primary">mdi-cash-register</v-icon>
      <span class="font-weight-medium">POS</span>
      <v-divider vertical class="mx-3" />
      <v-chip small label color="primary" outlined>
        <v-icon left small>mdi-warehouse</v-icon>
        {{ warehouse ? warehouse.name : use_language.warehouse_not_selected }}
      </v-chip>
      <v-btn small text @click="warehouseDialog = true">
        <v-icon left small>mdi-swap-horizontal</v-icon>{{ use_language.change }}
      </v-btn>
      <v-spacer />
      <v-chip
        v-if="channelLoaded && !posChannelId"
        small
        label
        color="red"
        outlined
        class="mr-3"
        :title="use_language.go_to_settings_add_channel + ' POS'"
      >
        <v-icon left small>mdi-alert</v-icon>
        {{ use_language.channel_not_set }} POS
      </v-chip>
      <span class="caption grey--text">
        Cashier: {{ staffId || '-' }}
      </span>
    </v-app-bar>

    <v-container fluid class="px-3">
      <v-row>
        <!-- ซ้าย: ช่องสแกน + แคตตาล็อกสินค้าที่กดเข้าตะกร้าได้ -->
        <v-col cols="12" md="8">
          <PosScanInput
            ref="scanInput"
            :warehouse-id="warehouse ? warehouse.id : null"
            :disabled="!warehouse"
            @add-item="addItem"
            @search="searchKeyword = $event"
          />
          <div class="mt-3">
            <PosProductGrid
              :warehouse-id="warehouse ? warehouse.id : null"
              :keyword="searchKeyword"
              @add-item="addItem"
            />
          </div>
        </v-col>

        <!-- ขวา: ลูกค้า + ตะกร้า + สรุปยอด (ติดหนึบตอนเลื่อนดูสินค้า) -->
        <v-col cols="12" md="4">
          <div class="pos-side">
            <PosCustomerPicker :customer="customer" @select="customer = $event" class="mb-3" />
            <PosCart
              ref="cart"
              :items="items"
              class="mb-3"
              @update-item="updateItem"
              @remove="removeItem"
              @clear="clearCart"
            />
            <PosCheckoutPanel
              :items="items"
              :order-discount="orderDiscount"
              :loading="submitting"
              :disabled="!items.length || !warehouse || submitting"
              @update:orderDiscount="orderDiscount = $event"
              @charge="charge"
            />
          </div>
        </v-col>
      </v-row>
    </v-container>

    <PosPaymentDialog
      v-model="paymentDialog"
      :total="grandTotal()"
      :loading="submitting"
      @confirm="submitOrder"
    />

    <PosWarehouseDialog
      v-model="warehouseDialog"
      :cancellable="!!warehouse"
      @select="onWarehouseSelected"
      @cancel="warehouseDialog = false"
    />
  </div>
</template>

<script>
import axios from 'axios'
import PosScanInput from './components/PosScanInput.vue'
import PosProductGrid from './components/PosProductGrid.vue'
import PosCart from './components/PosCart.vue'
import PosCustomerPicker from './components/PosCustomerPicker.vue'
import PosCheckoutPanel from './components/PosCheckoutPanel.vue'
import PosPaymentDialog from './components/PosPaymentDialog.vue'
import PosWarehouseDialog from './components/PosWarehouseDialog.vue'
import { orderService_dotnet, shopService_dotnet } from '@/website/global'
import { get_languages } from '@/website/global_function'

const CART_KEY = 'pos_cart_draft'
const WH_ID_KEY = 'pos_warehouse_id'
const WH_NAME_KEY = 'pos_warehouse_name'
const RECEIPT_KEY = 'pos_receipt_payload'

export default {
  name: 'PosPage',
  components: { PosScanInput, PosProductGrid, PosCart, PosCustomerPicker, PosCheckoutPanel, PosPaymentDialog, PosWarehouseDialog },
  data: () => ({
    use_language: {},
    header_token: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + localStorage.getItem('authorization_token') },
    shopId: null,
    branchId: null,
    staffId: null,
    shopInfo: null,
    shopAddress: null,
    posChannelId: null,
    channelLoaded: false,
    warehouse: null,
    warehouseDialog: false,
    paymentDialog: false,
    items: [],
    customer: null,
    orderDiscount: 0,
    submitting: false,
    searchKeyword: ''
  }),
  async created () {
    this.use_language = await this.get_languages(localStorage.getItem("set_language"))
    this.shopId = localStorage.getItem('shop_id')
    this.branchId = localStorage.getItem('branch_id')
    this.staffId = localStorage.getItem('staff_id')

    const whId = localStorage.getItem(WH_ID_KEY)
    const whName = localStorage.getItem(WH_NAME_KEY)
    if (whId && whName) {
      this.warehouse = { id: Number(whId), name: whName }
    } else {
      this.warehouseDialog = true
    }

    try {
      const raw = localStorage.getItem(CART_KEY)
      if (raw) this.items = JSON.parse(raw) || []
    } catch (e) {
      this.items = []
    }

    this.loadShopInfo()
  },
  watch: {
    items: {
      deep: true,
      handler (v) {
        try { localStorage.setItem(CART_KEY, JSON.stringify(v)) } catch (e) { /* quota */ }
      }
    }
  },
  methods: {
    get_languages,
    async loadShopInfo () {
      try {
        const [resShop, resAddress, resChannel] = await Promise.all([
          axios.post(
            shopService_dotnet + 'Shop/get-shop',
            { shopID: this.shopId },
            { headers: this.header_token }
          ),
          axios.post(
            shopService_dotnet + 'ShopAddress/get-shop-address-list',
            { shopID: this.shopId },
            { headers: this.header_token }
          ),
          axios.post(
            shopService_dotnet + 'ShopSetting/get-shop-channelSource-setting',
            { shopID: this.shopId },
            { headers: this.header_token }
          )
        ])
        if (resShop.status === 200) this.shopInfo = resShop.data
        const list = resAddress.data || []
        this.shopAddress = list.find(a => a.isDefault) || list[list.length - 1] || null

        // ช่องทางขายของออเดอร์ POS — ร้านต้องตั้งช่องทางชื่อ "POS" ไว้ในหน้าตั้งค่าร้านค้า
        const channels = resChannel.data || []
        const pos = channels.find(
          c => String(c.channelName || '').trim().toUpperCase() === 'POS'
        )
        this.posChannelId = pos ? pos.id : null
        this.channelLoaded = true
      } catch (e) { /* best-effort */ }
    },
    formatShopAddress (a) {
      if (!a) return null
      const isBangkok = Number(a.provinceId) === 2
      return [
        a.address,
        a.subDistrict ? (isBangkok ? 'แขวง' : 'ตำบล') + a.subDistrict : null,
        a.district ? (isBangkok ? 'เขต' : 'อำเภอ') + a.district : null,
        a.province ? 'จังหวัด' + a.province : null,
        a.postcode
      ].filter(Boolean).join(' ')
    },
    onWarehouseSelected (w) {
      this.warehouse = w
      localStorage.setItem(WH_ID_KEY, String(w.id))
      localStorage.setItem(WH_NAME_KEY, w.name)
      this.$nextTick(() => {
        if (this.$refs.scanInput) this.$refs.scanInput.focus()
      })
    },
    addItem (stock) {
      const existing = this.items.find(it =>
        it.stockShopID === stock.stockShopID &&
        it.productMasterItemID === stock.productMasterItemID
      )
      if (existing) {
        existing.quantityInput = Number(existing.quantityInput || 0) + 1
        this.revealItem(this.items.indexOf(existing))
      } else {
        this.items.push({
          stockShopID: stock.stockShopID,
          productMasterId: stock.productMasterId ?? stock.productMasterID,
          productMasterItemID: stock.productMasterItemID,
          name: stock.name,
          sku: stock.sku,
          barcode: stock.barcode,
          photoLink: stock.photoLink || null,
          unitPrice: Number(stock.unitPrice || 0),
          salePrice: Number(stock.salePrice || stock.unitPrice || 0),
          quantityInput: 1,
          discount: 0,
          discountPercent: 0,
          availableQuantity: stock.quantityAvailable ?? stock.availableQuantity ?? 0
        })
        this.revealItem(this.items.length - 1)
      }
    },
    // เลื่อนตะกร้าไปให้เห็นรายการที่เพิ่งสแกน (รายการใหม่ต่อท้ายเสมอ)
    revealItem (index) {
      this.$nextTick(() => {
        if (this.$refs.cart) this.$refs.cart.scrollToItem(index)
      })
    },
    updateItem ({ index, patch }) {
      const it = this.items[index]
      if (!it) return
      this.$set(this.items, index, { ...it, ...patch })
    },
    removeItem (i) {
      this.items.splice(i, 1)
    },
    clearCart () {
      this.items = []
      this.orderDiscount = 0
    },
    subtotal () {
      return this.items.reduce(
        (s, it) => s + Number(it.salePrice || 0) * Number(it.quantityInput || 0), 0
      )
    },
    itemDiscount () {
      return this.items.reduce(
        (s, it) => s + Number(it.discount || 0) * Number(it.quantityInput || 0), 0
      )
    },
    grandTotal () {
      return Math.max(0, this.subtotal() - this.itemDiscount() - Number(this.orderDiscount || 0))
    },
    // กดปุ่มเก็บเงิน → ตรวจสต๊อกก่อน แล้วเปิด dialog ให้เลือกวิธีชำระ
    charge () {
      if (!this.items.length || !this.warehouse) return

      const overstocked = this.items.find(it =>
        it.availableQuantity != null && Number(it.quantityInput) > Number(it.availableQuantity)
      )
      if (overstocked) {
        this.$swal({
          position: 'top',
          type: 'warning',
          title: this.use_language.remaining_insufficient,
          text: overstocked.name + ' ' + this.use_language.balance + ' ' + overstocked.availableQuantity + ')'
        })
        return
      }

      this.paymentDialog = true
    },
    async submitOrder (payment) {
      if (!this.items.length || !this.warehouse) return

      this.submitting = true
      try {
        const total = this.grandTotal()
        const createToken = String(Date.now()) + '-' + Math.random().toString(36).slice(2, 8)
        const now = new Date()
        const pad = n => String(n).padStart(2, '0')
        const y = now.getFullYear()
        const mo = pad(now.getMonth() + 1)
        const d = pad(now.getDate())
        const hms = pad(now.getHours()) + ':' + pad(now.getMinutes()) + ':' + pad(now.getSeconds())
        // POS จ่ายจบหน้าร้าน → เวลาแจ้งชำระ = เวลาที่บันทึกออเดอร์
        const informDatetime = y + '-' + mo + '-' + d + ' ' + hms
        const informDatetimeShow = d + '-' + mo + '-' + y + ' ' + hms

        const orderMaster = {
          shopId: Number(this.shopId),
          branchId: this.branchId ? Number(this.branchId) : null,
          createdByStaffId: this.staffId ? Number(this.staffId) : null,
          salemanId: this.staffId ? Number(this.staffId) : null,
          shippingStatusId: 0,
          totalPrice: this.subtotal(),
          deliveryCost: 0,
          orderDiscountPercent: null,
          orderDiscount: this.itemDiscount() + Number(this.orderDiscount || 0),
          vatType: null,
          vatpercent: null,
          vatAmount: null,
          shopAddressId: null,
          shippingAddressId: null,
          shippingAddressInvoiceId: null,
          isCOD: false,
          codamount: null,
          insuranceAmount: null,
          remark: 'POS',
          remarkCustomer: '',
          remarkWarehouse: '',
          shopBankAccountId: null,
          expressCompanyId: null,
          branchExpressID: null,
          shopExpressID: null,
          referenceSourceId: this.posChannelId || 0,
          warehouseId: this.warehouse.id,
          referenceNumber: payment.referenceNumber,
          taxID: null,
          ExpressUniqueID: null,
          createToken
        }

        const orderItem = this.items.map(it => ({
          id: 0,
          orderMasterId: 0,
          stockShopId: it.stockShopID,
          quantity: Number(it.quantityInput),
          unitPrice: Number(it.salePrice),
          salePrice: Number(it.salePrice),
          discountPercent: Number(it.discountPercent || 0),
          discount: Number(it.discount || 0) * Number(it.quantityInput),
          isLock: true,
          productMasterID: it.productMasterId,
          productMasterItemID: it.productMasterItemID,
          isProductSet: false,
          name: null
        }))

        const orderPayment = [{
          id: 1,
          orderMasterId: 0,
          bankAccountText: payment.referenceNumber,
          bankAccountID: null,
          amount: total,
          photoLink: null,
          informDatetime,
          informDatetimeShow,
          dueDate: null,
          dueDateShow: null,
          statusID: 4,
          remark: payment.remark
        }]

        const res = await axios.post(
          orderService_dotnet + 'Order/new-order',
          {
            orderMaster,
            orderItem,
            shopBankAccount: null,
            shippingAddress: null,
            shippingAddressInvoice: null,
            oldShippingAddress: null,
            shopAddress: null,
            orderPayment,
            orderDoc: null,
            b2bInsert: null
          },
          { headers: this.header_token }
        )

        if (res.status === 200 && res.data && res.data.status === 'success') {
          const receipt = {
            orderId: res.data.orderID,
            items: this.items.map(it => ({
              name: it.name,
              sku: it.sku,
              barcode: it.barcode,
              quantity: Number(it.quantityInput),
              salePrice: Number(it.salePrice),
              discount: Number(it.discount || 0) * Number(it.quantityInput),
              lineTotal: Math.max(
                0,
                Number(it.salePrice) * Number(it.quantityInput) -
                  Number(it.discount || 0) * Number(it.quantityInput)
              )
            })),
            customer: this.customer
              ? { name: this.customer.name, phone: this.customer.phone, code: this.customer.customerCode }
              : { name: 'Walk-in', phone: null, code: null },
            warehouseName: this.warehouse.name,
            cashierId: this.staffId,
            subtotal: this.subtotal(),
            itemDiscount: this.itemDiscount(),
            orderDiscount: Number(this.orderDiscount || 0),
            grandTotal: total,
            paymentLabel: payment.methodLabel,
            paymentReference: payment.referenceNumber,
            receivedAmount: payment.receivedAmount,
            changeAmount: payment.changeAmount,
            createdAt: new Date().toISOString(),
            shopName: this.shopInfo ? this.shopInfo.name : null,
            shopPhone: (this.shopAddress && this.shopAddress.phone) ||
              (this.shopInfo ? this.shopInfo.phone : null),
            shopAddress: this.formatShopAddress(this.shopAddress)
          }
          localStorage.setItem(RECEIPT_KEY, JSON.stringify(receipt))
          window.open('/pos-receipt', '_blank')

          this.paymentDialog = false
          this.clearCart()
          this.customer = null
          localStorage.removeItem(CART_KEY)
          this.$nextTick(() => {
            if (this.$refs.scanInput) this.$refs.scanInput.focus()
          })
        } else if (res.data && res.data.orderItemProductResponse && res.data.orderItemProductResponse.length) {
          this.$swal({
            position: 'top',
            type: 'warning',
            title: this.use_language.insufficient_stock,
            text: res.data.error_description || this.use_language.insufficient_stock_2
          })
        } else {
          this.$swal({
            position: 'top',
            type: 'error',
            text: (res.data && res.data.error_description) || this.use_language.cannot_perform_operation
          })
        }
      } catch (err) {
        this.$swal({ position: 'top', type: 'error', text: err.message })
      }
      this.submitting = false
    }
  }
}
</script>

<style scoped>
.pos-page {
  background: #f5f5f5;
  min-height: 100vh;
}
@media (min-width: 541px) {
  .pos-page {
    margin-left: -30px;
    margin-right: -30px;
  }
}
.pos-toolbar {
  border-bottom: 1px solid #e0e0e0;
}
/* คอลัมน์ขวาติดหนึบ เลื่อนดูสินค้าฝั่งซ้ายแล้วยังเห็นตะกร้า/ยอดสุทธิตลอด */
@media (min-width: 960px) {
  .pos-side {
    position: sticky;
    top: 60px;
  }
}
</style>
